import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../state/GameProvider';
import { DrawingCanvas } from '../components/DrawingCanvas';
import type { RevealMode } from '@share-paint/shared';

const PERSIST_KEY = 'share-paint:identity';
const hasIdentity = () =>
  typeof localStorage !== 'undefined' && !!localStorage.getItem(PERSIST_KEY);

// Igual que Game y Voting: 900×600 lógico.
const CANVAS_W = 900;
const CANVAS_H = 600;

export function Reveal() {
  const { state, leaveSession } = useGame();
  const nav = useNavigate();
  const [mode, setMode] = useState<RevealMode>('all');
  const [target, setTarget] = useState<string | null>(null);

  const players = state.session?.players ?? [];
  const drawings = state.finalDrawings;
  const tally = state.tally;
  const winnerId = tally[0]?.drawingId;

  const rankedDrawings = useMemo(() => {
    return [...drawings].sort((a, b) => {
      const sa = tally.find((t) => t.drawingId === a.id)?.score ?? 0;
      const sb = tally.find((t) => t.drawingId === b.id)?.score ?? 0;
      return sb - sa;
    });
  }, [drawings, tally]);

  const backHome = () => {
    leaveSession();
    nav('/', { replace: true });
  };

  // Si tras 2.5s no tenemos drawings NI identidad → volver al inicio.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!state.session && state.finalDrawings.length === 0 && !hasIdentity()) {
        nav('/', { replace: true });
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [state.session, state.finalDrawings.length, nav]);

  if (!state.me)
    return <div className="min-h-screen flex items-center justify-center text-white/60">Cargando…</div>;

  return (
    <main className="min-h-screen p-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase text-white/40">Modo revelación</p>
          <h2 className="text-3xl font-bold">Resultados finales 🏆</h2>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button onClick={() => setMode('all')} className={modeBtn(mode === 'all')}>Por colores</button>
          <button onClick={() => setMode('anon')} className={modeBtn(mode === 'anon')}>Anónimo</button>
          <button onClick={() => setMode('byUser')} className={modeBtn(mode === 'byUser')}>Por autor</button>
          <span className="mx-1 h-6 w-px bg-white/10" />
          <button onClick={backHome} className="btn-ghost">↩ Volver al inicio</button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {rankedDrawings.map((d, idx) => {
          const t = tally.find((x) => x.drawingId === d.id);
          const author = players.find((p) => p.id === d.authorId);
          const isWinner = d.id === winnerId;
          return (
            <div key={d.id} className={`card ${isWinner ? 'ring-2 ring-accent' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className={`text-3xl font-bold ${isWinner ? 'text-accent' : 'text-white/40'}`}>
                    #{idx + 1}
                  </span>
                  <div>
                    {isWinner && <p className="text-xs uppercase text-accent font-bold">🏆 Ganador</p>}
                    <p className="text-sm mt-1 flex items-center gap-2">
                      {mode === 'all' && author && (
                        <>
                          <span className="w-3 h-3 rounded-full" style={{ background: author.color }} />
                          <span className="font-medium">{author.username}</span>
                        </>
                      )}
                      {mode === 'anon' && <span className="text-white/40 italic">anónimo</span>}
                      {mode === 'byUser' && (
                        <select
                          className="input py-1 text-sm"
                          onChange={(e) => setTarget(e.target.value)}
                          defaultValue=""
                        >
                          <option value="" disabled>Elegir jugador…</option>
                          {players.map((p) => (
                            <option key={p.id} value={p.id}>{p.username}</option>
                          ))}
                        </select>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-mono ${isWinner ? 'text-accent' : ''}`}>{t?.score ?? 0}</p>
                  <p className="text-xs text-white/40">votos</p>
                </div>
              </div>

              <div className="flex justify-center bg-white/5 rounded-lg p-2">
                <DrawingCanvas
                  width={CANVAS_W}
                  height={CANVAS_H}
                  strokes={d.strokes}
                  myUserId={state.me!.id}
                  players={players}
                  disabled
                  revealMode={mode}
                  revealTargetId={target}
                />
              </div>
            </div>
          );
        })}
      </section>

      <footer className="mt-8 text-center text-white/40 text-xs">
        Prompt: "{state.session?.prompt}" · {players.length} jugadores · {state.totalRounds} rondas
      </footer>
    </main>
  );
}

function modeBtn(active: boolean) {
  return active ? 'btn-primary' : 'btn-ghost';
}
